import { ISchedule } from '@tableau/self-service-schedules/lib/src/components/ISchedule';
import { ActivitySpinnerSize, ActivitySpinnerWidget, FilteringList, ListItem } from '@tableau/widgets';
import * as React from 'react';
import { Messages } from '../../codegen/Localize';
import { ReactActionDialogBodyProps } from '../../commonInfra/dialogs/ReactActionDialog';
import { cssClass } from '../../style';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { IScheduleInfo } from '../types/IScheduleInfo';
import * as _ from 'underscore';

export interface SchedulePickerProps {
  readonly scheduledActionType: string;
  readonly useDescriptionAsName: boolean;
  readonly getSchedules: (matchText: string) => ResourceQuery<ISchedule>;
  readonly description: string;
}

export interface SchedulePickerState {
  schedule: ISchedule;
  selectedScheduleOption: string;
}

export enum SubscriptionFrequency {
  onSelectedSchedule = 'selectedSchedule',
  onExtractRefresh = 'extractRefresh'
}

export type PropsForSchedulePickerActionBody = ReactActionDialogBodyProps<SchedulePickerProps, SchedulePickerState | undefined>;

export const INITIAL_SLICE_SIZE = 20;
export const SLICE_SIZE = 1000;

export function toListItem(useDescriptionAsName: boolean, schedule: ISchedule): ListItem {
  return {
    value: schedule.id,
    content: useDescriptionAsName
      ? IScheduleInfo.getScheduleDescription(schedule)
      : schedule.name,
  };
}

export function matchesSearch(searchText: string, item: ListItem): boolean {
  if (searchText === '') {
    return true;
  }
  const words = searchText.toLocaleLowerCase().split(' ');
  const name = item.content as string;
  return _.all(words, (word) => name.toLocaleLowerCase().indexOf(word) !== -1);
}

// Adapted from src\app\content\SetOwnerDialog.tsx for selecting Schedules
export function SchedulePickerActionBody(props: PropsForSchedulePickerActionBody) {
  const [searchText, setSearchText] = React.useState('');
  const [schedules, setSchedules] = React.useState([] as ISchedule[]);
  const [scheduleListItems, setScheduleListItems] = React.useState([] as ListItem[]);
  const [showSpinner, setShowSpinner] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState(undefined as string);
  const listMapper = (item: ISchedule) => toListItem(props.useDescriptionAsName, item);
  const listFilterer = (item: ListItem) => matchesSearch(searchText, item);


  const onSelectSchedule = React.useCallback(
    (value: string) => {
      const item = schedules.find((item) => item.id === value);
      if (item) {
        props.updateDialogState({ schedule: item });
        setSelectedId(value);
      }
    },
    [props.updateDialogState, schedules],
  );

  const onSchedulePickerFilterChange = React.useCallback(
    (value: string) => setSearchText(value),
    [],
  );

  const hasSearchText = () => !_.isUndefined(searchText) && searchText.length !== 0;

  React.useEffect(
    () => {
      let ignore = false;
      async function fetchSchedules(start: number, size: number) {
        const isFirstFetch = schedules.length === 0;
        if (isFirstFetch) { // Starting from scratch, no errors
          setIsError(false);
        }
        try {
          const schedulesResult = await props.getSchedules(searchText).getSlice(start, size);
          const newSchedules: ISchedule[] = schedulesResult.items;
          if (!ignore) {
            setShowSpinner(false);
            setSchedules((schedules) => schedules.concat(newSchedules));
            const newListItems = newSchedules.map(listMapper);
            setScheduleListItems((listItems) => listItems.concat(newListItems));
          }
          if (schedulesResult.total > start + newSchedules.length) {
            fetchSchedules(start + newSchedules.length, SLICE_SIZE);
          }
        } catch (e) {
          setShowSpinner(false);
          setIsError(true);
        }
      }

      fetchSchedules(0, INITIAL_SLICE_SIZE);
      return () => { ignore = true; };
    },
    // No dependence on search text since we do client side filtering
    []
  );

  return <>
    <p>{props.description}</p>
    <div style={{ minHeight: 50 }}> {/* Needed to stop it from just collapsing when getting few/no results */}
      <FilteringList
        items={scheduleListItems.filter(listFilterer)}
        onSelect={onSelectSchedule}
        searchPlaceholder={Messages.search()}
        testId="filtering-list-set-schedule"
        style={{ height: '100%', maxHeight: '300px', width: 'auto', border: '1px solid #b2b2b2' }}
        onFilterChange={onSchedulePickerFilterChange}
        searchText={searchText}
        inputTestId="schedule-picker-filter"
        listTestId="schedule-picker-list"
      >
        {!showSpinner && (schedules.length === 0 || isError) &&
          <div className={noResults} data-tb-test-id="set-schedules-no-results">
            {hasSearchText() ? Messages.noMatchingResults() : Messages.noSchedulesAvailable()}
          </div>
        }
        {showSpinner &&
          <div className={spinner}>
            <ActivitySpinnerWidget
              spinnerSize={ActivitySpinnerSize.Large}
              testId={'owner-picker'}
            />
          </div>
        }
      </FilteringList>
    </div>
  </>;
}

const spinner = cssClass('set-owner-dialog', 'spinner', {
  marginTop: 18,
  textAlign: 'center'
});

const noResults = cssClass('set-owner-dialog', 'no-results', {
  fontSize: '15px',
  marginTop: 18,
  padding: 6,
  lineHeight: '23px',
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minHeight: 10,
});
